import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { TipusArea } from '@/api/types';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit } from '@/pages/operacio/comu';
import { Permis } from '@/permisos/Permis';
import { useGuardaCanvis } from './marc/canvisContext';
import { useEstructura, type TipusNode } from './marc/estructura';
import { Arbre } from './estructura/Arbre';
import { nodesVisibles, type FiltreArbre } from './estructura/arbreModel';
import { NouNode } from './estructura/Calaixos';
import { FitxaArea, FitxaDepartament, FitxaServei } from './estructura/Fitxes';
import {
  clauNode,
  llegeixClau,
  paramsDeSeleccio,
  seleccioDeParams,
  type Seleccio,
} from './estructura/suport';
import './administracio.css';
import './estructura.css';

/**
 * Estructura (W-24): arbre Departament › Àrea › Servei a l'esquerra i fitxa del node triat a la
 * dreta. Cada fill es crea des del seu pare («＋»), i el calaix diu on es crearà.
 * El node triat va a l'URL (`?servei=10`, `?area=1`, `?departament=2`; també `?node=SERVEI-10`)
 * perquè altres pantalles hi puguin enllaçar directament.
 */
export function W24Arees() {
  const { t } = useTranslation('administracio');
  const e = useEstructura();
  const guarda = useGuardaCanvis();
  const [params, setParams] = useSearchParams();
  const [filtre, setFiltre] = useState<FiltreArbre>({ text: '', tipus: '', inactius: false });
  // Plegat de l'arbre: els avantpassats del node triat es despleguen sols (en entrar per enllaç
  // o després de crear-lo); a més, l'usuari pot obrir o tancar qualsevol node a mà.
  const [oberts, setOberts] = useState<Set<string>>(new Set());
  const [tancats, setTancats] = useState<Set<string>>(new Set());
  const [nou, setNou] = useState<{ tipus: TipusNode; pareId?: number } | null>(null);

  const primer = e.departaments[0];
  const desUrl = seleccioDeParams(params);
  const clauUrl = desUrl ? clauNode(desUrl) : null;
  const seleccio = useMemo<Seleccio | null>(
    () => llegeixClau(clauUrl) ?? (primer ? { tipus: 'DEPARTAMENT', id: primer.id } : null),
    [clauUrl, primer],
  );
  // En arribar per enllaç (o en canviar de node), la fila triada es fa visible a l'arbre.
  const clauSel = seleccio ? clauNode(seleccio) : null;
  useEffect(() => {
    if (!clauSel || e.carregant) return;
    document
      .querySelector(`.est-arbre [data-node="${clauSel}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [clauSel, e.carregant]);

  const area =
    seleccio?.tipus === 'AREA'
      ? e.area(seleccio.id)
      : seleccio?.tipus === 'SERVEI'
        ? e.area(e.servei(seleccio.id)?.areaId)
        : undefined;
  const departament =
    seleccio?.tipus === 'DEPARTAMENT'
      ? e.departament(seleccio.id)
      : e.departament(area?.departamentId);
  const servei = seleccio?.tipus === 'SERVEI' ? e.servei(seleccio.id) : undefined;

  const avantpassats = [
    departament ? clauNode({ tipus: 'DEPARTAMENT', id: departament.id }) : null,
    area && seleccio?.tipus === 'SERVEI' ? clauNode({ tipus: 'AREA', id: area.id }) : null,
  ].filter((x): x is string => !!x);
  const obertsEfectius = new Set([...oberts, ...avantpassats.filter((c) => !tancats.has(c))]);

  const nodes = useMemo(
    () => nodesVisibles(e.departaments, filtre, seleccio, t),
    [e.departaments, filtre, seleccio, t],
  );

  const selecciona = (s: Seleccio) => {
    if (seleccio && clauNode(s) === clauNode(seleccio)) return;
    guarda(() => {
      setTancats(new Set());
      setParams(paramsDeSeleccio(s), { replace: true });
    });
  };
  const commuta = (clau: string) => {
    const obert = obertsEfectius.has(clau);
    setOberts((o) => {
      const n = new Set(o);
      if (obert) n.delete(clau);
      else n.add(clau);
      return n;
    });
    setTancats((o) => {
      const n = new Set(o);
      if (obert) n.add(clau);
      else n.delete(clau);
      return n;
    });
  };
  const accions = {
    onSelecciona: selecciona,
    onNou: (tipus: TipusNode, pareId: number) => setNou({ tipus, pareId }),
  };

  if (e.error) {
    return (
      <section className="pantalla" data-screen="W-24">
        <Toolbar titol={t('titols.arees')} />
        <ErrorCarrega onReintenta={e.refetch} />
      </section>
    );
  }

  let fitxa = <Buit icona="building">{t('estructura.tria')}</Buit>;
  if (seleccio?.tipus === 'DEPARTAMENT' && departament)
    fitxa = <FitxaDepartament key={clauNode(seleccio)} departament={departament} {...accions} />;
  else if (seleccio?.tipus === 'AREA' && area)
    fitxa = (
      <FitxaArea key={clauNode(seleccio)} area={area} departament={departament} {...accions} />
    );
  else if (seleccio?.tipus === 'SERVEI' && servei)
    fitxa = (
      <FitxaServei
        key={clauNode(seleccio)}
        servei={servei}
        area={area}
        departament={departament}
        {...accions}
      />
    );

  const filtrant = filtre.text.trim() !== '' || filtre.tipus !== '';

  return (
    <section className="pantalla" data-screen="W-24">
      <Toolbar titol={t('titols.arees')}>
        <Permis recurs="cai.organitzacio" tipus="ALTA">
          <button
            type="button"
            className="btn btn--outline btn--s"
            onClick={() => setNou({ tipus: 'DEPARTAMENT' })}
          >
            <Icona nom="plus" />
            {t('estructura.nouDepartament')}
          </button>
        </Permis>
      </Toolbar>
      <div className="lf est">
        <aside className="lf__llista">
          <div className="lf__eines">
            <label className="search">
              <Icona nom="magnifier" />
              <input
                type="search"
                value={filtre.text}
                onChange={(ev) => setFiltre({ ...filtre, text: ev.target.value })}
                placeholder={t('estructura.cerca')}
                aria-label={t('estructura.cerca')}
              />
            </label>
            <div className="est-filtres" role="group" aria-label={t('estructura.filtres.etiqueta')}>
              {(['', 'GUARDIA_IT', 'CRISI'] as (TipusArea | '')[]).map((tp) => (
                <button
                  key={tp || 'tot'}
                  type="button"
                  className="chip"
                  aria-pressed={filtre.tipus === tp}
                  onClick={() => setFiltre({ ...filtre, tipus: tp })}
                >
                  {tp ? (
                    <span
                      className={`node-punt node-punt--${tp.toLowerCase()}`}
                      aria-hidden="true"
                    />
                  ) : null}
                  {t(`estructura.filtres.${tp || 'tot'}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="est-arbre__scroll">
            {e.carregant ? (
              <Buit icona="building">{t('comu.carregant')}</Buit>
            ) : e.departaments.length === 0 ? (
              <Buit icona="building">{t('estructura.buit')}</Buit>
            ) : nodes.length === 0 ? (
              <Buit icona="magnifier">
                {filtre.text.trim()
                  ? t('estructura.senseResultats', { q: filtre.text.trim() })
                  : t('estructura.senseFiltre')}
              </Buit>
            ) : (
              <Arbre
                nodes={nodes}
                seleccio={seleccio}
                oberts={obertsEfectius}
                totObert={filtrant}
                onCommuta={commuta}
                onSelecciona={selecciona}
                onNou={accions.onNou}
              />
            )}
          </div>
          <div className="est-peu">
            <label className="check">
              <input
                type="checkbox"
                className="switch"
                checked={filtre.inactius}
                onChange={(ev) => setFiltre({ ...filtre, inactius: ev.target.checked })}
              />
              {t('estructura.mostraInactius')}
            </label>
            <span className="est-llegenda">
              <span>
                <span className="node-punt node-punt--guardia_it" aria-hidden="true" />
                {t('estructura.filtres.GUARDIA_IT')}
              </span>
              <span>
                <span className="node-punt node-punt--crisi" aria-hidden="true" />
                {t('estructura.filtres.CRISI')}
              </span>
            </span>
          </div>
        </aside>
        {fitxa}
      </div>
      {nou ? (
        <NouNode
          tipus={nou.tipus}
          pareId={nou.pareId}
          onTanca={() => setNou(null)}
          onCreat={(s) => setParams(paramsDeSeleccio(s), { replace: true })}
        />
      ) : null}
    </section>
  );
}
